import * as React from 'react';
import { observer } from 'mobx-react';
import { TrackersData, ITracker, TRACKER_DATE_FORMAT, displayedDailyValue, IDailyValue, TRACKER_SRC, conversionForTracker } from 'data/TrackersData';
import * as moment from 'moment';
import { GraphNav } from 'components/mywellness/weekly-graph/GraphNav';
import { TrackerWeeklyGraph } from 'components/mywellness/weekly-graph/GraphedTracker';
import { computed, observable } from 'mobx';
import { DayLabels } from 'components/mywellness/weekly-graph/DayLabels';

import './TrackerDetailSubPage.pcss';
import { IconButton } from 'components/widgets/IconButton';
import { Button } from 'components/widgets/Button';
import { FloatInput } from 'components/widgets/TextInput';
import {UIState} from "data/UIState";
import {Alert} from "../../data/Alert";

@observer
class TrackerDetailSubPage extends React.Component {

  @observable tab:string = TRACKER_SRC.FITBIT;

  constructor(props) {
    super(props);
    TrackersData.weeklyMoment = moment().startOf('week');
    if(TrackersData.selectedTracker && TrackersData.selectedTracker.src===TRACKER_SRC.FITBIT) {
      this.tab = TRACKER_SRC.FITBIT;
    } else {
      this.tab = TRACKER_SRC.MANUAL;
    }
  }

  @computed
  get thisWeeklyMoment() {
    return moment().startOf('week');
  }

  @computed
  get weeklyMomentsToDisplay() {
    const weeklyMoments:Array<moment.Moment> = [];
    if(TrackersData.firstWeeklyMoment && this.thisWeeklyMoment) {
      weeklyMoments.push(this.thisWeeklyMoment);
      let weeklyMoment = moment(this.thisWeeklyMoment).subtract(1,'week');
      while(weeklyMoment.isAfter(TrackersData.firstWeeklyMoment)) {
        weeklyMoments.push(moment(weeklyMoment));
        weeklyMoment.subtract(1,'week');
      }
    }
    return weeklyMoments;
  }

  @computed 
  get hasManualData() {
    return TrackersData.selectedTracker && Object.keys(TrackersData.selectedTracker.dailyValues).length>0;
  }

  @computed
  get showTabs() {
    return TrackersData.selectedTracker && TrackersData.selectedTracker.src===TRACKER_SRC.FITBIT && this.hasManualData;
  }

  render() {
    const tracker = TrackersData.selectedTracker;
    if(tracker) {
      return (
        <div className="trackerDetail">
          <div className="top">
            <GraphNav />
            <DayLabels />
            <TrackerWeeklyGraph tracker={tracker} />
          </div>
          {this.showTabs &&
          <div className="srcTabs noselect">
            <a className={`fitbit${this.tab===TRACKER_SRC.FITBIT?' selected':''}`} onClick={()=>{ this.tab=TRACKER_SRC.FITBIT; }}><i className="material-icons">lens</i> FITBIT</a>
            <a className={`manual${this.tab===TRACKER_SRC.MANUAL?' selected':''}`} onClick={()=>{ this.tab=TRACKER_SRC.MANUAL; }}><i className="material-icons">lens</i> MANUAL</a>
          </div>
          }          
          {this.weeklyMomentsToDisplay.map((weeklyMoment)=><WeekTrackerBlock fitbit={this.tab===TRACKER_SRC.FITBIT} key={weeklyMoment.toDate().getTime()} tracker={tracker} weekStart={weeklyMoment} />)}
        </div>
      );
    } else {
      return null
    }
  } 
} 


@observer
class WeekTrackerBlock extends React.Component<{tracker:ITracker, weekStart:moment.Moment, alwaysShow?:boolean, fitbit:boolean}> {

  @computed
  get headline() {
    const { weekStart } = this.props;
    if(this.props.weekStart.isSame(moment().startOf('week'), 'week')) {
      return "THIS WEEK";
    } else if(this.props.weekStart.isSame(moment().subtract(1, 'week'), 'week')) {
      return "LAST WEEK";
    } else {
      const lastDay = moment(this.props.weekStart).endOf('week');
      if(weekStart.isSame(lastDay, 'month')) {
        return `${weekStart.format('MMM D')}–${lastDay.format('D')}`;
      } else {
        return `${weekStart.format('MMM D')}–${lastDay.format('MMM D')}`;
      }
    }
  }


  @computed
  get weekHasTrackers() {
    let weekHasTrackers = false;    
    if(this.dailyValues.length>0){
      for(let i=0;i<this.dailyValues.length;i++) {
        if(this.dailyValues[i].value>0)  {
          weekHasTrackers = true;
          break;
        }   
      }
    }
    return weekHasTrackers;
  }

  @computed 
  get dailyValues() {
    const dailyValues:Array<IDailyValue> = [];
    for(let i=0;i<7;i++) {
      const day = moment(this.props.weekStart).add(i,'d');

      const possibleDV = (this.props.fitbit) ? this.props.tracker.dailyValuesFitbit[day.format(TRACKER_DATE_FORMAT)] : this.props.tracker.dailyValues[day.format(TRACKER_DATE_FORMAT)];

      if(possibleDV) {
        dailyValues.push(possibleDV);
      } else {
        dailyValues.push({
          value:0,
          day:day.toDate(),
          src:this.props.tracker.src
        });
      }
    }
    return dailyValues.reverse();    
  }

  @observable editingIndexes:Array<boolean> = [false, false, false, false, false, false, false, false];

  handleEditStateChange = (index:number, editState:boolean) => { 
    this.editingIndexes[index-1] = editState;
  }

  render() {
    if(this.weekHasTrackers || this.props.alwaysShow) {

      return (
        <React.Fragment>
          <h2>{this.headline}</h2>
          <div className="weekTrackerBlock">
            {this.dailyValues.map((dailyValue, idx)=><DayLine key={idx} index={idx} nextDayLineInEditMode={this.editingIndexes[idx]} onEditStateChange={this.handleEditStateChange} dailyValue={dailyValue} tracker={this.props.tracker} fitbit={this.props.fitbit} />)}
          </div>
        </React.Fragment>
      );
    } else {
      return null;
    }
  }
}

interface DayLine_P {
  dailyValue:IDailyValue;
  tracker:ITracker;
  fitbit:boolean;
  onEditStateChange:(index:number, editState:boolean)=>void;
  index:number;
  nextDayLineInEditMode:boolean;
}

@observer
class DayLine extends React.Component<DayLine_P> {  

  @observable loaded:boolean = false;
  @observable _editing:boolean = false;
  @observable saving:boolean = false;
  @observable value:number;
  @observable startingValue:number;
  private clear:number;

  @computed
  get editing() {
    return this._editing;
  }

  set editing(_editing:boolean) {
    this._editing = _editing;
    this.props.onEditStateChange(this.props.index, _editing)
  }

  constructor(props:DayLine_P) {
    super(props);
    if(props.dailyValue) {          
      this.value = this.converter(props.dailyValue.value);
      this.startingValue = this.converter(props.dailyValue.value);
    } else {
      this.value = this.clear;
      this.startingValue = this.clear;
    }        
  }

  @computed 
  get isDirty() {
    return this.startingValue!==this.value;
  }

  componentDidMount() {
    setTimeout(()=>{
      this.loaded = true;
    },1);
  }

  @computed  
  get percentageComplete():number {    
    if(this.loaded) {
      const convertedValue = this.converter(this.value, true);
      if(convertedValue) {
        const goal = 1*this.props.tracker.dailyGoal;
        const val = 1*convertedValue;
        if(val >= goal) {
          return 100;
        } else {
          return Math.round( (val/goal) * 100 );
        }
      } else {
        return 0;
      } 
    } else {
      return 0;
    }
  }

  @computed
  get percentageIncomplete() {
    return 100-this.percentageComplete;
  }

  @computed 
  get displayedDailyValue() {
    return displayedDailyValue(this.props.tracker, this.props.dailyValue);
  }

  @computed
  get converter() {
    const converter = conversionForTracker(this.props.tracker.id);
    if(converter) {
      return converter;
    } else {
      return (val, reverse?:boolean)=>val;
    }
  }

  handleChangeValue = (v:number) => {    
    this.value = v;
  }

  handleClickEdit = () =>{
    this.editing = true;
    //this.props.onEditStateChange(this.props.index, true);
  }

  handleUpdateClick = () => {
    this.saving = true;
    TrackersData.updateDailyValue(this.props.tracker, { value:this.converter(this.value, true), day:this.props.dailyValue.day, src:TRACKER_SRC.MANUAL})
    .then(()=>{
      this.startingValue = this.value;
      this.editing = false;
      //this.props.onEditStateChange(this.props.index, false);
    })
    .catch(()=>{
      Alert.alert(`Failed to update ${this.props.tracker.name}. Please make sure your Internet connection is stable and try again.`);
    })
    .then(()=>{
      this.saving = false;
    });
  }

  handleClickRevert = () => {
    this.value = this.startingValue;
  }

  render() {
    return (
      <div className={`dayLine${this.editing?' editing':''}${this.props.nextDayLineInEditMode && !this.editing ? ' nextEditing':''}`}>
        <div className="inner">
          <h3>{moment(this.props.dailyValue.day).format('ddd M/D')}</h3>
          <div className={`bar${this.props.fitbit?' fitbit':''}`}><span style={{width: `${this.percentageComplete}%`}} className={this.percentageComplete===100?'full':''}></span></div>          
            {this.editing ? 
              <React.Fragment>
                <div className={`data${this.isDirty?' dirty':''}`}>
                  <FloatInput disabled={this.saving} value={this.value} onChangeValue={this.handleChangeValue} />
                  {this.isDirty && <a className="revert noselect" onClick={this.handleClickRevert}><i className="material-icons">close</i></a> }
                  <div className="units noselect">{this.props.tracker.unit}s</div>
                </div>
                <Button disabled={this.saving} onClick={this.handleUpdateClick}>update</Button>
              </React.Fragment>
            :
              <React.Fragment>
                <h4>{this.value} {this.props.tracker.unit}s</h4>
                {!this.props.fitbit && this.loaded && <IconButton onClick={this.handleClickEdit} className="link2">edit</IconButton>}
              </React.Fragment>
            }            
        </div>
      </div>
    );
  }


}






export { TrackerDetailSubPage };
